<template>
  <transition name="slideout-search">
    <div
      v-show="slideoutSearchOpen"
      class="predictive-search-wrapper"
    >
      <div class="predictive-search">
        <div class="predictive-search__header">
          <form
            ref="form"
            :action="route"
            method="get"
            role="search"
            class="predictive-search__form"
          >
            <input
              type="hidden"
              name="type"
              value="product"
            >
            <label
              for="Search"
              class="visually-hidden"
            >
              {{ strings.title }}
            </label>
            <input
              id="Search"
              ref="searchInput"
              v-model="searchQuery"
              type="search"
              name="q"
              :placeholder="strings.placeholder"
              class="predictive-search__input"
              role="combobox"
              aria-expanded="false"
              aria-owns="predictive-search-results"
              aria-controls="predictive-search-results"
              aria-haspopup="listbox"
              aria-autocomplete="list"
              @keyup="updateSearchQuery($event)"
            >
            <div class="predictive-search__active-line" />
          </form>

          <button
            class="predictive-search__close close-btn xs font-semibold flex items-center absolute top-0 bottom-0 my-auto right-0"
            @click="closeSlideoutSearch"
          >
            Close
            <CloseIcon />
          </button>
        </div>

        <div
          v-if="resultsFound"
          class="predictive-search__result-count-bar flex justify-between items-center"
        >
          <p class="predictive-search__title">
            {{ resultsCountText }}
          </p>

          <button
            class="predictive-search__title underline"
            @click="submitForm"
          >
            View ALL results
          </button>
        </div>
        <div
          v-else-if="searching"
          class="predictive-search__no-results"
        >
          <p class="predictive-search__title">
            Searching...
          </p>
        </div>
        <div
          v-else-if="noResultsFound"
          class="predictive-search__no-results"
        >
          <p class="predictive-search__title">
            No results could be found.
          </p>
        </div>

        <div
          class="predictive-search__grid"
          :class="{ 'predictive-search__grid--searching': searching }"
        >
          <div class="predictive-search__main">
            <div
              v-if="popularSearchTerms && searchQuery === ''"
              class="predictive-search__popular-searches"
            >
              <h2 class="predictive-search__title">
                {{ strings.popularSearches }}
              </h2>
              <ul class="predictive-search__popular-searches-terms flex">
                <li
                  v-for="popularSearchTerm in popularSearchTerms"
                  :key="popularSearchTerm"
                  class="predictive-search__popular-search-term"
                >
                  <button
                    class="predictive-search__popular-search-term-button"
                    @click="quickSearch(popularSearchTerm)"
                  >
                    {{ popularSearchTerm }}
                  </button>
                </li>
              </ul>
            </div>
            <div
              v-if="noResultsFound || searchQuery === ''"
              class="predictive-search__popular-products"
            >
              <h2 class="predictive-search__title">
                {{ strings.popularProducts }}
              </h2>
              <div
                v-if="popularProducts"
                class="grid grid-cols-2 md:grid-cols-4"
              >
                <ProductItem
                  v-for="product in popularProducts"
                  :key="`propular-product-search-${product.handle}`"
                  :product="product"
                  :is-search-result="true"
                />
              </div>
            </div>
            <div
              v-else-if="productResults"
              class="predictive-search__product-results grid grid-cols-2 sm:grid-cols-3"
              :class="{ 'md:grid-cols-3': hasNoneProductResults, 'md:grid-cols-4': !hasNoneProductResults }"
            >
              <ProductItem
                v-for="product in productResults"
                :key="`search-result-product-${product.handle}`"
                :product="product"
                :is-search-result="true"
              />
            </div>
          </div>
          <div
            v-if="hasNoneProductResults"
            class="predictive-search__other"
          >
            <ul class="sm:grid grid-cols-2 gap-4 sm">
              <li v-if="searchResultsFormatted.collections">
                <h2 class="predictive-search__title">
                  Collections
                </h2>
                <ul>
                  <li
                    v-for="collection in searchResultsFormatted.collections"
                    :key="`search-result-collection${collection.id}`"
                  >
                    <a :href="collection.url">
                      {{ collection.title }}
                    </a>
                  </li>
                </ul>
              </li>
              <li v-if="searchResultsFormatted.pages">
                <h2 class="predictive-search__title">
                  Pages
                </h2>
                <ul>
                  <li
                    v-for="page in searchResultsFormatted.pages"
                    :key="`search-result-page${page.id}`"
                  >
                    <a :href="page.url">
                      {{ page.title }}
                    </a>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import CloseIcon from '../icons/close-icon'
import ProductItem from './product-item'

import axios from 'axios'

import { useSlideoutSearchStore } from '../pinia/SlideoutSearchStore'
import { mapActions, mapState } from 'pinia'

export default {
  components: {
    CloseIcon,
    ProductItem
  },
  props: {
    popularProducts: {
      type: Array,
      default: null
    },
    popularSearchTerms: {
      type: Array,
      default: null
    },
    strings: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      searching: false,
      lastSearchQuery: '',
      searchQuery: '',
      route: window.theme.search.route,
      endpoint: '/search/suggest.json',
      searchTypes: ['products', 'collections', 'pages'],
      searchFields: 'title,tag,body',
      searchResults: {},
    }
  },
  computed: {
    ...mapState(useSlideoutSearchStore, ['slideoutSearchOpen']),

    resultsCount () {
      let count = 0
      for (let searchType in this.searchResultsFormatted) {
        if (this.searchResultsFormatted[searchType] !== null) {
          count += this.searchResultsFormatted[searchType].length
        }
      }

      return count
    },
    resultsCountText () {
      return `We found ${this.resultsCount} result${this.resultsCount > 1 ? 's' : ''}`
    },
    hasNoneProductResults () {
      return this.searchQuery !== '' && (this.searchResultsFormatted.collections || this.searchResultsFormatted.pages)
    },
    resultsFound () {
      return !this.searching && this.searchQuery !== '' && !!Object.keys(this.searchResultsFormatted).length
    },
    noResultsFound () {
      return !this.searching && this.searchQuery !== '' && !Object.keys(this.searchResultsFormatted).length
    },
    productResults () {
      let limit = 8
      if (this.hasNoneProductResults) limit = 6

      if (this.searchResultsFormatted.products) {
        return [...this.searchResultsFormatted.products].splice(0, limit)
      }
      return null
    },
    searchResultsFormatted () {
      const results = {}

      if (this.searchResults.resources) {
        this.searchTypes.forEach(searchType => {
          /*
            Filter results
          */
          let matchingResults = this.searchResults.resources.results[searchType].filter((result, index) => {
            const tags = result.tags || []

            /*
              Filter out products with:
                - tag 'seo-hidden'
                - title containing the word 'free'
            */
            if (tags.includes('seo-hidden')) return false
            if (result.title.toLowerCase().includes('free')) return false

            return true
          })

          if (matchingResults.length > 0) results[searchType] = matchingResults
        })
      }

      return results
    },
    searchQueryParameter () {
      return `?q=${this.searchQuery}`
    },
    searchQueryResourcesTypeParameter () {
      let resourcesType = '&resources[type]='

      this.searchTypes.forEach((searchType, index) => {
        resourcesType = `${resourcesType}${searchType.slice(0, -1)}`

        if (index < this.searchTypes.length - 1) resourcesType = `${resourcesType},`
      })

      return resourcesType
    },
    searchQueryEndpoint () {
      return `${this.endpoint}${this.searchQueryParameter}${this.searchQueryResourcesTypeParameter}&resources[options][fields]=${this.searchFields}`
    }
  },
  watch: {
    slideoutSearchOpen (open) {
      if (open) {
        setTimeout(() => {
          this.$refs.searchInput.focus()
        }, 1000)
      }
    }
  },
  mounted () {
    setInterval(async () => {
      if (this.searching) {
        await this.search()
        this.searching = false
      }
    }, 300)
  },
  methods: {
    ...mapActions(useSlideoutSearchStore, ['closeSlideoutSearch']),

    async search () {
      if (this.searchQuery.length > 0) {
        const searchResults = await axios.get(this.searchQueryEndpoint)
        this.searchResults = searchResults.data
        this.lastSearchQuery = this.searchQuery
      } else {
        this.searchResults = {}
      }
    },
    updateSearchQuery (event) {
      if (event.code !== 'Space') {
        if (this.searchQuery !== this.lastSearchQuery) {
          this.searching = true
        }
      }
    },
    quickSearch (query) {
      this.searchQuery = query
      this.searching = true
    },
    submitForm () {
      this.$refs.form.submit()
    }
  }
}
</script>
